import { Component, DestroyRef, OnInit } from '@angular/core';
import { API, TemplateGroupDTO, TemplateSourceEntryDTO, TemplateVariableDTO, TemplateVariableType } from '../../../../services/api/api';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Params } from '@angular/router';
import { ErrorService } from '../../../../services/error.service';
import { ModalService } from '../../../../services/gui/modal/modal.service';
import { UtilService } from '../../../../services/util.service';
import { NavigationService } from '@ngx-seatback/utils';
import { RoutingID } from '../../../../routes/sb-routing-enum';
import { SbDateTime } from '../../../../services/api/api-date-time';
import { QueryParamService } from '../../../../services/lists/query-params';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, take } from 'rxjs/operators';

@Component({
  selector: 'sb-page-template-source-entry-edit',
  templateUrl: './template-source-entry-edit.component.html',
  styleUrls: ['./template-source-entry-edit.component.scss'],
  standalone: false,
})
export class TemplateSourceEntryEditComponent implements OnInit {
  templateEntry: TemplateSourceEntryDTO;
  templateGroupId: number;
  templateSourceId: number;
  templateEntryId: number;
  loading: boolean;
  form: UntypedFormGroup;
  templateVariables: TemplateVariableDTO[];
  regex: RegExp;

  from: SbDateTime;
  till: SbDateTime;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly api: API,
    public readonly errorHandler: ErrorService,
    public readonly modal: ModalService,
    public readonly util: UtilService,
    public readonly navService: NavigationService,
    public readonly queryParamService: QueryParamService,
    private readonly destroyRef: DestroyRef
  ) {
    this.templateSourceId = null;
    this.templateEntryId = null;
    this.templateGroupId = null;
    this.loading = true;
    this.regex = new RegExp('http(s)?://([\\da-z.-]+)\\.([a-z.]{2,6})[/\\w .-]*/?');
    this.form = new UntypedFormGroup({
      Name: new UntypedFormControl('', [Validators.required]),
    });
  }

  get Name() {
    return this.form.get('Name');
  }

  ngOnInit(): void {
    this.templateSourceId = this.queryParamService.getQueryParam('templateSourceID');
    this.templateGroupId = this.queryParamService.getQueryParam('templateGroupID');
    this.route.params.subscribe((params: Params) => {
      this.templateEntryId = params.templateSourceEntryID;
      this.api.mm_templating_provider_TemplateGroup_retrieveById(this.templateGroupId).subscribe({
        next: (templateGroup: TemplateGroupDTO) => {
          if (templateGroup) {
            this.templateVariables = templateGroup.variables;
            this.refreshFromServer();
            this.registerFormChangesListener();
          }
        },
      });
    });
  }

  private registerFormChangesListener() {
    this.Name.valueChanges.subscribe(value => {
      this.templateEntry.name = value;
    });
  }

  updateForm() {
    this.Name.setValue(this.templateEntry.name);
    this.Name.markAsTouched();
  }

  private refreshFromServer() {
    if (!this.templateEntryId) {
      this.templateEntry = new TemplateSourceEntryDTO();
      this.templateEntry.values = {};
      for (const element of this.templateVariables) {
        this.templateEntry.values[element.name] = '';
      }
      this.loading = false;
    } else {
      this.api
        .mm_templating_provider_TemplateSourceEntry_retrieveAllInSource(this.templateSourceId, { ids: [this.templateEntryId] })
        .subscribe({
          next: (templateEntries: TemplateSourceEntryDTO[]) => {
            if (templateEntries && templateEntries.length > 0) {
              this.templateEntry = templateEntries[0];
              this.till = this.templateEntry.validTill;
              if (this.till) {
                this.from = this.templateEntry.createdAt;
              }
              this.updateForm();
            }
            this.loading = false;
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  updateTemplateSource() {
    this.loading = true;
    if (!this.templateEntry.id) {
      if (this.templateEntry.name) {
        this.api
          .mm_templating_provider_TemplateSourceEntry_createTemplateEntry(this.templateSourceId, this.templateEntry)
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            take(1),
            finalize(() => {
              this.loading = false;
            })
          )
          .subscribe({
            next: (createdTemplateEntry: TemplateSourceEntryDTO) => {
              this.templateEntry = createdTemplateEntry;
              this.navService.navigateToPoint(RoutingID.templateSourceEntries, [{ id: 'templateSourceID', value: this.templateSourceId }]);
            },
            error: error => {
              this.errorHandler.errorHandler(error);
            },
          });
      }
    } else if (this.templateEntry.name) {
      this.api
        .mm_templating_provider_TemplateSourceEntry_editInSource(this.templateSourceId, [this.templateEntry])
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          take(1),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe({
          next: (createdTemplateEntry: TemplateSourceEntryDTO[]) => {
            this.templateEntry = createdTemplateEntry[0];
            this.navService.navigateToPoint(RoutingID.templateSourceEntries, [
              {
                id: 'templateSourceID',
                value: this.templateSourceId,
              },
            ]);
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  validate(key: string, type: string): boolean {
    if (!key || !this.templateEntry) {
      return false;
    }

    const hasInput = this.templateEntry.values[key]?.length > 0 && this.templateEntry.values[key]?.trim()?.length > 0;
    switch (type) {
      case this.TemplateVariableType.TEXT:
        return hasInput;
      case this.TemplateVariableType.IMAGE:
        return this.regex && this.regex.test(this.templateEntry.values[key]) && hasInput;
      case undefined:
      default:
        return false;
    }
  }

  handleDateChange(dates) {
    this.templateEntry.validFrom = dates.fromDate;
    this.templateEntry.validTill = dates.toDate;
  }

  get TemplateVariableType() {
    return TemplateVariableType;
  }

  isAnyVariableInvalid(): boolean {
    return this.templateVariables.some(variable => {
      return !this.validate(variable.name, variable.type);
    });
  }
}
